/**
 * Productos, precios e impuestos.
 *
 * La decisión que gobierna todo este archivo: **el precio guardado es el
 * precio final, con el impuesto ya adentro**. Es como está construida la base
 * de precios del negocio y como se le habla al cliente — "la empanada vale
 * $3.700", no "$3.426 más INC".
 *
 * De ahí se saca hacia atrás la base gravable y el impuesto, que es lo que
 * exige la cotización y lo que va a necesitar la facturación electrónica
 * cuando llegue. Hacerlo al revés —guardar la base y sumar— produce precios
 * finales con centavos, y nadie cobra $3.699,99 por una empanada.
 */
export const IMPUESTOS = [
    { tipo: 'INC', tasa: 0.08, texto: 'INC 8%', nota: 'Consumo en tienda: comida preparada.' },
    { tipo: 'IVA', tasa: 0.19, texto: 'IVA 19%', nota: 'Congelados de carne, pollo y cocteleras.' },
    { tipo: 'IVA', tasa: 0.05, texto: 'IVA 5%', nota: 'Congelados de queso, dedos y panzerotti.' },
    { tipo: 'excluido', tasa: 0, texto: 'Excluido', nota: 'Sin impuesto. Servicios como el domicilio.' },
];
// ------------------------------------------------------------- cuentas
/** Pesos enteros. El dinero nunca se guarda ni se calcula con decimales. */
export const pesos = (n) => Math.round(n);
/**
 * La foto que representa al producto.
 *
 * La señalada en `foto_principal` si existe entre las fotos; si no, la
 * primera. Lo segundo cubre dos casos reales: el producto con una sola foto al
 * que nadie le marcó nada, y la principal que se borró sin volver a escoger.
 * En los dos, mostrar algo es mejor que mostrar un hueco.
 */
export function fotoPrincipal(p) {
    const fotos = p.fotos ?? [];
    if (fotos.length === 0)
        return undefined;
    return fotos.find((f) => f.ruta === p.foto_principal) ?? fotos[0];
}
export function baseGravable(precioConImpuesto, tasa) {
    if (tasa <= 0)
        return pesos(precioConImpuesto);
    return pesos(precioConImpuesto / (1 + tasa));
}
export function valorImpuesto(precioConImpuesto, tasa) {
    return pesos(precioConImpuesto) - baseGravable(precioConImpuesto, tasa);
}
export function calcularLinea(precioUnitario, cantidad, tasa) {
    // El impuesto se calcula sobre el total de la línea y no por unidad: hacerlo
    // por unidad y multiplicar acumula el error del redondeo en cada fila.
    const total = pesos(precioUnitario * cantidad);
    const base = baseGravable(total, tasa);
    return { total, base, impuesto: total - base };
}
/**
 * Suma un documento agrupando por tasa de impuesto.
 *
 * Agrupar importa: un pedido con empanadas de tienda (INC 8%) y congeladas
 * (IVA 19%) tiene dos bases gravables distintas, y meterlas en un solo saco
 * da un impuesto equivocado.
 */
export function calcularTotales(lineas) {
    const grupos = new Map();
    for (const l of lineas) {
        const llave = `${l.impuesto.tipo}:${l.impuesto.tasa}`;
        const g = grupos.get(llave) ?? { tipo: l.impuesto.tipo, tasa: l.impuesto.tasa, total: 0 };
        g.total += pesos(l.precio_unitario * l.cantidad);
        grupos.set(llave, g);
    }
    const porTasa = [...grupos.values()].map((g) => {
        const base = baseGravable(g.total, g.tasa);
        return { tipo: g.tipo, tasa: g.tasa, base, impuesto: g.total - base };
    });
    const total = porTasa.reduce((s, g) => s + g.base + g.impuesto, 0);
    return {
        total,
        base: porTasa.reduce((s, g) => s + g.base, 0),
        impuestos: porTasa.reduce((s, g) => s + g.impuesto, 0),
        porTasa,
    };
}
/**
 * Margen sobre la venta, en porcentaje: (venta − costo) ÷ venta. Solo para ojos
 * internos.
 *
 * La «venta» es el precio SIN impuesto: el impuesto es del Estado, no del
 * negocio, y contarlo como ingreso inflaría el margen. Es la misma definición
 * del margen bruto del Tablero y del estado de resultados, para que una
 * empanada no tenga un margen en el inventario y otro en los informes.
 *
 * Antes se calculaba sobre el costo (ganancia ÷ costo), que es un recargo, no un
 * margen: una Cajita de $14.900 con costo $7.508 salía en 84 % cuando de cada
 * $100 vendidos sin impuesto quedan $45,6.
 *
 * Sin costo o sin precio no hay margen que calcular: devuelve null, no 0 ni 100.
 */
export function margen(precioVenta, costo, tasa) {
    if (!costo)
        return null;
    const base = baseGravable(precioVenta, tasa);
    if (!(base > 0))
        return null;
    return ((base - costo) / base) * 100;
}
/** "$ 45.000" — en pesos colombianos, sin decimales. */
export function enPesos(n) {
    return new Intl.NumberFormat('es-CO', {
        style: 'currency',
        currency: 'COP',
        maximumFractionDigits: 0,
    }).format(n);
}
/**
 * "11,68" — una cantidad de inventario, en español.
 *
 * Un saldo de insumo puede llevar fracción: 11,68 bolsas de café son once
 * bolsas y un poco de la que está abierta. Pintarlo como lo escribe
 * JavaScript, «11.683», se lee en Colombia como once mil seiscientas. Dos
 * decimales como máximo y sin ceros de relleno: un saldo entero sigue siendo
 * «12», no «12,00».
 */
export function enCantidad(n) {
    // «−0» no es un saldo: sale cuando se resta todo y queda el ruido de la coma flotante.
    const limpio = Math.abs(n) < 0.005 ? 0 : n;
    return new Intl.NumberFormat('es-CO', { maximumFractionDigits: 2 }).format(limpio);
}
// ------------------------------------------------------------- combos
/** Lo que costaría comprar los componentes por separado, a precio de lista. */
export function precioListaCombo(partes) {
    return pesos(partes.reduce((s, p) => s + p.precio_venta * p.cantidad, 0));
}
/** Cuánto se ahorra el cliente contra comprarlos sueltos. Negativo = el combo
 *  está más caro que sus partes, que casi siempre es un error de digitación. */
export function ahorroCombo(precioCombo, partes) {
    const lista = precioListaCombo(partes);
    if (!lista)
        return null;
    const dif = lista - pesos(precioCombo);
    return { pesos: dif, porcentaje: (dif / lista) * 100 };
}
/**
 * Una sugerencia de impuesto para un combo, no una regla.
 *
 * Devuelve la tasa más baja de los componentes. Sirve como punto de partida
 * cuando se arma un combo, y nada más: **el impuesto de un combo lo escoge una
 * persona**, porque no depende de lo que hay adentro sino del concepto de la
 * venta.
 *
 * El caso que lo dejó claro: la misma empanada de carne paga INC 8% cuando se
 * come en el local y pasa a IVA cuando se vende empacada para llevar —5% si
 * lleva queso, 19% si no—. Es el mismo producto, el mismo costo y la misma
 * receta; lo que cambió es que dejó de ser un servicio de restaurante y pasó a
 * ser la venta de un bien. Ninguna función que mire los componentes puede
 * deducir eso, y pretender que sí llevaba a cobrar la tasa equivocada con toda
 * la confianza del mundo.
 */
export function impuestoDeCombo(partes) {
    if (partes.length === 0)
        return null;
    let menor = partes[0].impuesto;
    for (const p of partes) {
        if (p.impuesto.tasa < menor.tasa)
            menor = p.impuesto;
    }
    return { tipo: menor.tipo, tasa: menor.tasa };
}
/** Las tasas distintas que trae un combo. Si trae más de una, hubo que escoger. */
export function tasasDelCombo(partes) {
    const vistas = new Map();
    for (const p of partes) {
        vistas.set(`${p.impuesto.tipo}:${p.impuesto.tasa}`, p.impuesto);
    }
    return [...vistas.values()].sort((a, b) => a.tasa - b.tasa);
}
/**
 * Convierte líneas de combos en líneas de productos reales.
 *
 * **Un combo nunca tiene saldo propio.** No existe «un combo» en la nevera:
 * existen las empanadas y existe el café. Por eso todo lo que toca inventario
 * —vender, entrar mercancía, trasladar, dar de baja— pasa primero por aquí.
 *
 * Es la misma regla en los dos sentidos, y esa simetría importa: si al vender
 * un combo se descuentan sus componentes pero al comprarlo se sumara el combo,
 * el saldo se desangraría con cada venta sin que nada lo repusiera, y nadie
 * entendería por qué.
 *
 * Un paquete de 30 empanadas es exactamente el mismo caso: es un combo de un
 * solo componente con cantidad 30. Sumar dos paquetes suma sesenta empanadas.
 *
 * Las cantidades del mismo código se suman, para que una operación que toca la
 * empanada suelta y la misma empanada dentro de un combo produzca un solo
 * movimiento y no dos. Dos no estarían mal contados, pero el historial se lee
 * peor sin ganar nada.
 *
 * Un producto que ya no esté en el catálogo pasa tal cual, por su código: lo
 * que ocurrió, ocurrió, y el inventario tiene que reflejarlo aunque alguien
 * haya borrado el producto entre tanto.
 */
export function explotarCombos(lineas, producto) {
    const suma = new Map();
    const sumar = (codigo, nombre, cantidad) => {
        if (cantidad <= 0)
            return;
        const y = suma.get(codigo);
        if (y)
            y.cantidad += cantidad;
        else
            suma.set(codigo, { codigo, nombre, cantidad });
    };
    for (const l of lineas) {
        const p = producto(l.codigo);
        const componentes = p?.tipo === 'combo' ? p.componentes ?? [] : [];
        // Lo que el vendedor escogió en los bloques: viene en la línea, no en el catálogo.
        const escogidos = p?.tipo === 'combo' ? l.elecciones ?? [] : [];
        if (componentes.length > 0 || escogidos.length > 0) {
            for (const c of componentes)
                sumar(c.codigo, c.nombre, c.cantidad * l.cantidad);
            for (const e of escogidos)
                sumar(e.codigo, e.nombre, e.cantidad * l.cantidad);
        }
        else {
            sumar(l.codigo, l.nombre, l.cantidad);
        }
    }
    return [...suma.values()];
}
/** ¿Este producto se explota en otros al tocar inventario? */
export function esCombo(p) {
    return p?.tipo === 'combo' && ((p.componentes?.length ?? 0) > 0 || (p.grupos?.length ?? 0) > 0);
}
/**
 * De qué clase es un producto.
 *
 * El insumo gana sobre el combo si un documento trajera las dos marcas, y el
 * orden no es arbitrario: lo que decide es si la cosa TIENE SALDO. Un insumo
 * lo tiene, y mostrarlo como combo —que por definición no tiene— lo sacaría de
 * la lista donde se cuenta.
 */
export function claseDeProducto(p) {
    if (p?.es_insumo)
        return 'insumo';
    if (esCombo(p))
        return 'combo';
    return 'simple';
}
export const CLASES_PRODUCTO = [
    { valor: 'simple', texto: 'Productos', nota: 'Lo que se vende y descarga su receta.' },
    { valor: 'combo', texto: 'Combos', nota: 'Se venden armados y no tienen saldo propio.' },
    { valor: 'insumo', texto: 'Insumos', nota: 'Lo que se almacena, se consume y se cuenta.' },
];
export function textoClaseProducto(c) {
    return CLASES_PRODUCTO.find((x) => x.valor === c)?.texto ?? c;
}
